import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { formatRecaudoDate } from "../../model/recaudoModel";
import { createPaymentReceiptPdf } from "../../utils/paymentReceiptPdf";
import logoImage from "../../../../../assets/images/logo horinzontal.png";

const PaymentReceiptModal = ({ open, onClose, transactionData, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Función para obtener el ID de transacción
  const getTransactionId = (transaction) => {
    if (!transaction) return "No disponible";
    if (transaction.paymentId) return transaction.paymentId;
    if (transaction.paymentIdRender?.value) return transaction.paymentIdRender.value;
    if (transaction.original?.paymentId) return transaction.original.paymentId;
    if (transaction.original?.metadata?.id) return transaction.original.metadata.id;
    if (transaction.id) return transaction.id.toString();
    return "No disponible";
  };

  // Función para obtener la información formateada para el PDF
  const getReceiptData = (data) => {
    if (!data) return null;

    const transactionId = getTransactionId(data);

    return {
      facturaNumero: data.billNumber && data.billNumber !== "-" ? data.billNumber : transactionId,
      empresa: data.company || "No disponible",
      nit: data.nit || "No disponible",
      monto: typeof data.amount === "number" ? data.amount : 0,
      fecha: data.createdAtFormatted || formatRecaudoDate(data.createdAt),
      transactionId: transactionId,
      estado: data.statusText || "Aprobado",
    };
  };

  // Función para formatear moneda
  const formatCOP = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  // Función para convertir imagen a Base64
  const getLogoBase64 = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      };
      img.onerror = () => {
        console.warn("Error cargando logo, usando texto alternativo");
        resolve(null);
      };
      img.src = logoImage;
    });
  };

  // Función para generar y descargar el PDF
  const handleDownloadPDF = async () => {
    if (!transactionData) {
      setError("No hay datos de transacción para generar el PDF");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const receiptData = getReceiptData(transactionData);
      const logoBase64 = await getLogoBase64();
      if (!receiptData) throw new Error("No se pudieron procesar los datos de la transacción");
      const doc = createPaymentReceiptPdf(receiptData, { logoDataUrl: logoBase64 });

      // Generar nombre del archivo
      const fileName = `comprobante_pago_${String(receiptData.transactionId).replace(
        /[^a-zA-Z0-9]/g,
        "_"
      )}.pdf`;

      // Descargar el PDF
      doc.save(fileName);

      if (onSuccess) {
        onSuccess("PDF descargado correctamente");
      }
    } catch (err) {
      console.error("Error generando PDF:", err);
      setError(`Error al generar el PDF: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!transactionData) return null;

  const receiptData = getReceiptData(transactionData);
  if (!receiptData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="receipt-modal-title"
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh", m: { xs: 1, sm: 3 } } }}
    >
      <DialogTitle
        component="div"
        sx={{ px: 3, py: 2, bgcolor: "#193e54", display: "flex", alignItems: "center", gap: 1.5 }}
      >
        <Box sx={{ display: "flex", bgcolor: "#ffffff18", color: "#fff", p: 1, borderRadius: 2 }}>
          <PdfIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            id="receipt-modal-title"
            component="h2"
            sx={{ fontSize: "1rem", fontWeight: 700, color: "#fff" }}
          >
            Comprobante de pago
          </Typography>
          <Typography sx={{ fontSize: ".75rem", color: "#dbe8f2", mt: 0.25 }}>
            Recaudo · Detalle de la transacción
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Cerrar comprobante" sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f5f7fa" }}>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box id="receipt-content" sx={{ pt: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box
              component="img"
              src={logoImage}
              alt="GoPass"
              sx={{ width: 120, height: 38, objectFit: "contain" }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Typography sx={{ fontSize: ".72rem", color: "#526579" }}>
              Valores en pesos colombianos
            </Typography>
          </Box>
          <Paper
            elevation={0}
            sx={{ p: 2.5, mb: 2, bgcolor: "#edf7f1", border: "1px solid #c8e3d2", borderRadius: 2 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography sx={{ color: "#38624a", fontSize: ".75rem", mb: 0.5 }}>
                  Valor de la transacción
                </Typography>
                <Typography
                  sx={{
                    color: "#17623a",
                    fontSize: { xs: "1.7rem", sm: "2rem" },
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  {formatCOP(receiptData.monto)}
                </Typography>
              </Box>
              <Chip
                label={receiptData.estado}
                size="small"
                sx={{
                  color: transactionData.statusCategory === "conciliated" ? "#205b85" : "#17623a",
                  bgcolor: transactionData.statusCategory === "conciliated" ? "#e1edf7" : "#d5eddf",
                  fontWeight: 700,
                  border: "1px solid #b9d7c4",
                }}
              />
            </Box>
          </Paper>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #dce5ed", borderRadius: 2 }}>
            <Typography
              component="h3"
              sx={{ color: "#193e54", fontWeight: 700, fontSize: ".85rem", mb: 2 }}
            >
              Información del pago
            </Typography>
            <Grid component="dl" container spacing={2.5} sx={{ mb: 0 }}>
              {[
                ["Número de factura", receiptData.facturaNumero],
                ["Fecha y hora · Bogotá", receiptData.fecha],
                ["Empresa / Usuario", receiptData.empresa],
                ["NIT / Identificación", receiptData.nit],
              ].map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Typography component="dt" sx={{ color: "#526579", fontSize: ".72rem", mb: 0.5 }}>
                    {label}
                  </Typography>
                  <Typography
                    component="dd"
                    sx={{
                      m: 0,
                      color: "#243b53",
                      fontSize: ".875rem",
                      fontWeight: 600,
                      lineHeight: 1.5,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ color: "#526579", fontSize: ".72rem", mb: 0.75 }}>
              ID de transacción
            </Typography>
            <Box sx={{ bgcolor: "#f3f6f9", p: 1.25, borderRadius: 1 }}>
              <Typography
                sx={{
                  color: "#354d63",
                  fontFamily: "monospace",
                  fontSize: ".78rem",
                  overflowWrap: "anywhere",
                }}
              >
                {receiptData.transactionId}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: "1px solid #dce5ed",
          bgcolor: "#fff",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ color: "#354d63", borderColor: "#c6d3df", borderRadius: 1.5 }}
        >
          Cerrar
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadPDF}
          disabled={loading}
          sx={{
            color: "#fff",
            bgcolor: "#17623a",
            borderRadius: 1.5,
            "&:hover": { bgcolor: "#124d2e" },
            "&.Mui-disabled": { color: "#526579" },
          }}
        >
          {loading ? "Generando PDF…" : "Descargar PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// PropTypes validation
PaymentReceiptModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  transactionData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    nit: PropTypes.string,
    company: PropTypes.string,
    amount: PropTypes.number,
    statusText: PropTypes.string,
    statusCategory: PropTypes.string,
    billNumber: PropTypes.string,
    createdAtFormatted: PropTypes.string,
    createdAt: PropTypes.string,
    paymentId: PropTypes.string,
    paymentIdRender: PropTypes.shape({
      value: PropTypes.string,
      display: PropTypes.string,
      isLink: PropTypes.bool,
      tooltip: PropTypes.string,
      fullId: PropTypes.string,
    }),
    original: PropTypes.shape({
      paymentId: PropTypes.string,
      metadata: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  }),
  onSuccess: PropTypes.func,
};

PaymentReceiptModal.defaultProps = {
  transactionData: null,
  onSuccess: null,
};

export default PaymentReceiptModal;
